import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { sendEvent } from '@blocket/tracking'
import { useDebounce } from 'react-use'
import { uniqBy } from 'lodash'

const initialState = []
export const PlacementsContext = createContext(initialState)

const EVENT_DEFAULTS = {
  eventCategory: 'internal-promotions',
  eventAction: 'internal-promotion-action',
  event: 'event',
  eventType: 'View',
  objectType: 'UIElement',
  objectId: 'internal-promotions',
}

const DEFAULT_TRACKING_TIMEOUT = 10000

export const createPromotionEventModel = ({ type, id, variant, creative }) => ({
  id: `${type}-${id}-${variant}`,
  name: `${type}-${id}-${variant}`,
  creative,
  position: id,
})

export default function PlacementTrackingProvider({ children }) {
  const [queue, setQueue] = useState(initialState)
  const history = useHistory()

  const addToQueue = useCallback(
    (event) => {
      setQueue((prev) => [...prev, event])
    },
    [setQueue],
  )

  const trackQueue = useCallback(() => {
    if (queue.length === 0) {
      return
    }

    sendEvent({
      ...EVENT_DEFAULTS,
      ecommerce: {
        promoView: {
          // prevent duplicates caused by multiple rendering in certain devices,
          // the VI page erroneously tries to load small layout first and then the medium
          promotions: uniqBy(queue.map(createPromotionEventModel), 'id', 'position'),
        },
      },
    })
    setQueue([])

    // clear the promotions as there is indication they may be sticky between unique view events
    window.dataLayer.push({ ecommerce: { promoView: { promotions: [] } } })
  }, [queue])

  const trackClick = useCallback(
    (placementProperties, eventProperties = {}) => {
      // Track any existing events in the queue, because GA needs these promotions to have been tracked before the click
      trackQueue()

      sendEvent({
        ...EVENT_DEFAULTS,
        ...eventProperties, // data might have been passed from implementor
        ecommerce: {
          promoClick: {
            promotions: [createPromotionEventModel(placementProperties)],
          },
        },
      })

      // clear the promotions as there is indication they may be sticky between unique view events
      window.dataLayer.push({ ecommerce: { promoClick: { promotions: [] } } })
    },
    [trackQueue],
  )

  useDebounce(
    () => {
      trackQueue()
    },
    DEFAULT_TRACKING_TIMEOUT,
    [queue],
  )

  useEffect(() => {
    const unlisten = history.listen(trackQueue)
    window.addEventListener('beforeunload', trackQueue)
    return () => {
      unlisten()
      window.removeEventListener('beforeunload', trackQueue)
    }
  }, [history, trackQueue])

  const value = useMemo(() => ({ trackImpression: addToQueue, trackClick }), [
    addToQueue,
    trackClick,
  ])

  return <PlacementsContext.Provider value={value}>{children}</PlacementsContext.Provider>
}

PlacementTrackingProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
